import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { migrateLocalStorageToDatabase } from '@/utils/migrateLocalStorage';
import type { UserProfile, UserRole } from './types';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  userRole: UserRole;
  isLoading: boolean;
  isAdmin: () => boolean;
  isStaff: () => boolean;
  requiresPasswordChange: boolean;
  signOut: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  refreshSession: () => Promise<void>;
  forceRefreshAuth: () => Promise<void>;
  hasAccess: (requiredRole: UserRole | UserRole[]) => boolean;
  updateProfile: (updates: Partial<UserProfile>) => Promise<UserProfile | null>;
  updateModuleProgress: (moduleId: string, progress: number) => Promise<boolean>;
  enrollInCourse: (courseId: string) => Promise<boolean>;
  userModuleProgress: Array<{module_id: string, progress: number}>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const SimpleAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<UserRole>('user');
  const [isLoading, setIsLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('🔑 Fetching user profile for:', userId);
      
      // First try to fetch directly from profiles table
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('🔑 Error fetching user profile:', profileError);
        return null;
      }

      if (profile) {
        console.log('🔑 User profile retrieved:', profile.name);
        setProfile(profile);
        
        // Check if temporary password has expired
        if (profile.requires_password_change && profile.temporary_password_expires_at) {
          const expiryDate = new Date(profile.temporary_password_expires_at);
          if (expiryDate < new Date()) {
            console.log('🔑 Temporary password expired');
            toast.error('Your temporary password has expired. Please contact support.');
            await supabase.auth.signOut();
            return null;
          }
        }
        
        setRequiresPasswordChange(profile.requires_password_change || false);
        return profile;
      }

      // If no profile found, try to create one using the auth user data
      console.log('🔑 No profile found, attempting to create one');
      const { data: { user }, error: getUserError } = await supabase.auth.getUser();
      
      // If user doesn't exist in auth.users, clear the corrupted session
      if (getUserError || !user) {
        console.error('🔑 ❌ No valid user found for session - clearing corrupted session');
        await supabase.auth.signOut();
        throw new Error('Invalid session - user does not exist in auth.users');
      }
      
      if (user) {
        const { data: newProfile, error: createError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: user.email || '',
            name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
            is_approved: false
          })
          .select()
          .maybeSingle();

        if (createError) {
          console.error('🔑 Error creating profile:', createError);
          
          // Return minimal in-memory profile as fallback
          console.log('⚠️ Creating minimal fallback profile to prevent blank screen');
          const minimalProfile: UserProfile = {
            id: userId,
            email: user.email || '',
            name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
            is_approved: false,
            requires_password_change: false,
            created_at: new Date().toISOString()
          };
          setProfile(minimalProfile);
          setRequiresPasswordChange(false);
          return minimalProfile;
        }

        if (newProfile) {
          console.log('🔑 Profile created:', newProfile.name);
          setProfile(newProfile);
          setRequiresPasswordChange(false);
          return newProfile;
        }
      }
      
      // Last resort: return minimal profile
      console.log('⚠️ Could not create profile - using minimal fallback');
      const { data: { user: fallbackUser } } = await supabase.auth.getUser();
      if (fallbackUser) {
        const minimalProfile: UserProfile = {
          id: userId,
          email: fallbackUser.email || '',
          name: fallbackUser.email?.split('@')[0] || 'User',
          is_approved: false,
          requires_password_change: false,
          created_at: new Date().toISOString()
        };
        setProfile(minimalProfile);
        return minimalProfile;
      }
      
      return null;
    } catch (error) {
      console.error('🔑 Error in fetchUserProfile:', error);
      return null;
    }
  };

  const fetchUserRole = async (userId: string, clearCache = false): Promise<UserRole> => {
    try {
      console.log('🔑 Fetching user role for:', userId, 'clearCache:', clearCache);
      
      // Clear any cached role data if requested
      if (clearCache) {
        console.log('🧹 Clearing all role caches...');
        localStorage.removeItem(`user_role_${userId}`);
        sessionStorage.removeItem(`user_role_${userId}`);
        sessionStorage.removeItem('cached_user_role');
        localStorage.removeItem('cached_user_role');
      }
      
      // Use the get_user_role_safe function which handles role prioritization
      const { data: roleData, error } = await supabase
        .rpc('get_user_role_safe', { user_id_param: userId });

      if (error) {
        console.error('🔑 Error fetching user role:', error);
        return 'user';
      }

      const role = (roleData as UserRole) || 'user';
      console.log('🔑 ✅ User role retrieved from database:', role);
      console.log('🔑 📍 Role source: Fresh from database (not cached)');
      return role;
    } catch (error) {
      console.error('🔑 Error in fetchUserRole:', error);
      return 'user';
    }
  };

  const isAdmin = () => userRole === 'admin';
  const isStaff = () => userRole === 'staff' || userRole === 'admin';

  const refreshUserData = async () => {
    if (!user) return;
    try {
      await Promise.all([
        fetchUserProfile(user.id),
        fetchUserRole(user.id).then(setUserRole)
      ]);
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  const refreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
      } else {
        setSession(data.session);
        setUser(data.session?.user || null);
        
        // Refetch profile to get updated data including requires_password_change flag
        if (data.session?.user) {
          await fetchUserProfile(data.session.user.id);
        }
      }
    } catch (error) {
      console.error('Error in refreshSession:', error);
    }
  };

  const forceRefreshAuth = async () => {
    try {
      console.log('🔑 🔄 Force refreshing authentication and role (clearing all caches)...');
      setIsLoading(true);
      
      // Clear enhanced auth service cache
      try {
        const { enhancedAuthService } = await import('@/hooks/auth/enhancedAuthService');
        enhancedAuthService.clearCache();
        console.log('🧹 Enhanced auth cache cleared');
      } catch (e) {
        console.log('No enhanced auth service to clear');
      }
      
      // Refresh the session
      const { data: { session }, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('🔑 Error refreshing session:', error);
        return;
      }
      
      if (session?.user) {
        setSession(session);
        setUser(session.user);
        
        // Re-fetch profile and role with cache clearing
        await Promise.all([
          fetchUserProfile(session.user.id),
          fetchUserRole(session.user.id, true).then(role => {
            setUserRole(role);
            console.log('🔑 ✅ Role refreshed from database:', role);
            toast.success(`Role refreshed: ${role}`, { duration: 2000 });
          })
        ]);
      }
    } catch (error) {
      console.error('Error in forceRefreshAuth:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      setUser(null);
      setSession(null);
      setProfile(null);
      setUserRole('user');
      setRequiresPasswordChange(false);

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Error signing out:', error);
      }
    } catch (error) {
      console.error('Error in signOut:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const hasAccess = (requiredRole: UserRole | UserRole[]): boolean => {
    if (!userRole) return false;
    
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    
    for (const role of roles) {
      if (role === 'user') return true;
      if (role === 'staff' && (userRole === 'staff' || userRole === 'admin')) return true;
      if (role === 'admin' && userRole === 'admin') return true;
    }
    
    return false;
  };

  const updateProfile = async (updates: Partial<UserProfile>): Promise<UserProfile | null> => {
    return profile;
  };

  const updateModuleProgress = async (moduleId: string, progress: number): Promise<boolean> => {
    return true;
  };

  const enrollInCourse = async (courseId: string): Promise<boolean> => {
    return true;
  };

  const userModuleProgress = React.useMemo(() => {
    if (!profile?.progress) return [];
    return Object.entries(profile.progress).map(([module_id, progress]) => ({
      module_id,
      progress: typeof progress === 'number' ? progress : 0
    }));
  }, [profile?.progress]);

  useEffect(() => {
    let mounted = true;

    console.log('🔑 AUTH HOOK: Starting fast initialization');

    // Get initial session immediately
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('🔑 AUTH INIT: Session error:', error);
          if (mounted) setIsLoading(false);
          return;
        }

          console.log('🔑 AUTH INIT: Session found:', !!session?.user, session?.user?.email);
        
        // Validate session - verify user actually exists in auth.users
        if (session?.user) {
          const { data: { user }, error: userError } = await supabase.auth.getUser();
          if (userError || !user) {
            console.error('🔑 ❌ Invalid session detected - user does not exist. Clearing session...');
            await supabase.auth.signOut();
            sessionStorage.clear();
            localStorage.removeItem('fast_auth_cache');
            if (mounted) {
              setSession(null);
              setUser(null);
              setIsLoading(false);
            }
            return;
          }
        }
        
        if (mounted) {
          setSession(session);
          setUser(session?.user || null);
          
          // If we have a user, fetch profile AND role
          if (session?.user) {
            // Check for recent valid session cache (fast-path for returning users)
            const cachedAuth = sessionStorage.getItem('fast_auth_cache');
            if (cachedAuth) {
              try {
                const { profile, role, timestamp } = JSON.parse(cachedAuth);
                const age = Date.now() - timestamp;
                
                // If cache is less than 5 minutes old, use it immediately
                if (age < 5 * 60 * 1000) {
                  console.log('⚡ Using fast-path cached auth (age:', Math.round(age / 1000), 'seconds)');
                  setProfile(profile);
                  setUserRole(role);
                  setRequiresPasswordChange(profile.requires_password_change || false);
                  setIsLoading(false);
                  
                  // Validate/refresh in background
                  setTimeout(() => {
                    Promise.all([
                      fetchUserProfile(session.user.id),
                      fetchUserRole(session.user.id, true).then(freshRole => {
                        if (mounted && freshRole !== role) {
                          console.log('🔄 Background role update:', freshRole);
                          setUserRole(freshRole);
                        }
                      })
                    ]).catch(err => console.error('Background refresh error:', err));
                  }, 0);
                  return;
                }
              } catch (e) {
                console.log('Invalid cache, continuing normal flow');
              }
            }
            
            // Clear any stale cached data on initialization
            console.log('🧹 Clearing stale role caches on auth init...');
            localStorage.removeItem(`user_role_${session.user.id}`);
            sessionStorage.removeItem(`user_role_${session.user.id}`);
            sessionStorage.removeItem('cached_user_role');
            localStorage.removeItem('cached_user_role');
            
            // Reduced timeout to 3 seconds (from 10)
            const profilePromise = Promise.all([
              fetchUserProfile(session.user.id),
              fetchUserRole(session.user.id, true).then(role => {
                if (mounted) {
                  setUserRole(role);
                  console.log('🔑 📍 Initial role set:', role);
                }
                return role;
              }),
              migrateLocalStorageToDatabase(session.user.id)
            ]);
            
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Auth initialization timeout')), 3000)
            );
            
            try {
              const [fetchedProfile, fetchedRole] = await Promise.race([profilePromise, timeoutPromise]) as [UserProfile | null, UserRole, any];
              
              if (mounted) {
                // If profile is null, create minimal fallback
                if (!fetchedProfile) {
                  console.log('⚠️ Profile was null after fetch, creating minimal fallback');
                  const minimalProfile: UserProfile = {
                    id: session.user.id,
                    email: session.user.email || '',
                    name: session.user.email?.split('@')[0] || 'User',
                    is_approved: false,
                    requires_password_change: false,
                    created_at: new Date().toISOString()
                  };
                  setProfile(minimalProfile);
                } else {
                  // Cache successful auth for fast-path
                  sessionStorage.setItem('fast_auth_cache', JSON.stringify({
                    profile: fetchedProfile,
                    role: fetchedRole,
                    timestamp: Date.now()
                  }));
                }
              }
            } catch (error) {
              if (error instanceof Error && error.message === 'Auth initialization timeout') {
                console.error('🔑 ⏱️ Auth initialization timed out after 3s - using minimal profile');
                if (mounted) {
                  const minimalProfile: UserProfile = {
                    id: session.user.id,
                    email: session.user.email || '',
                    name: session.user.email?.split('@')[0] || 'User',
                    is_approved: false,
                    requires_password_change: false,
                    created_at: new Date().toISOString()
                  };
                  setProfile(minimalProfile);
                }
              } else {
                throw error;
              }
            }
          }
          
          // Stop loading
          setIsLoading(false);
        }
      } catch (error) {
        console.error('🔑 AUTH INIT: Initialization error:', error);
        if (mounted) setIsLoading(false);
      }
    };

    // Set up auth state listener - NOT async to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!mounted) return;

        console.log('🔑 AUTH STATE:', event, !!session?.user);
        
        setSession(session);
        setUser(session?.user || null);

        if (event === 'SIGNED_IN' && session?.user) {
          // Fetch profile and role in background using setTimeout to avoid blocking
          setTimeout(() => {
            if (!mounted) return;
            Promise.all([
              fetchUserProfile(session.user.id),
              fetchUserRole(session.user.id).then(role => {
                if (mounted) {
                  console.log('🔑 Setting role after SIGNED_IN:', role);
                  setUserRole(role);
                }
              }),
              migrateLocalStorageToDatabase(session.user.id)
            ]).finally(() => {
              if (mounted) setIsLoading(false);
            });
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setProfile(null);
          setUserRole('user');
          setRequiresPasswordChange(false);
          if (mounted) setIsLoading(false);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          // Refresh role in background
          setTimeout(() => {
            if (!mounted) return;
            fetchUserRole(session.user.id).then(role => {
              if (mounted) {
                console.log('🔑 Setting role after TOKEN_REFRESHED:', role);
                setUserRole(role);
              }
            }).finally(() => {
              if (mounted) setIsLoading(false);
            });
          }, 0);
        } else {
          // For other events, stop loading immediately
          if (mounted) setIsLoading(false);
        }
      }
    );

    // Initialize immediately
    initializeAuth();

    // Set backup timeout to ensure loading never blocks indefinitely
    const timeout = setTimeout(() => {
      if (mounted) {
        console.log('🔑 AUTH TIMEOUT: Forcing loading to false');
        setIsLoading(false);
      }
    }, 2000);

    return () => {
      mounted = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    session,
    profile,
    userRole,
    isLoading,
    isAdmin,
    isStaff,
    requiresPasswordChange,
    signOut,
    refreshUserData,
    refreshSession,
    forceRefreshAuth,
    hasAccess,
    updateProfile,
    updateModuleProgress,
    enrollInCourse,
    userModuleProgress,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useSupabaseAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SimpleAuthProvider');
  }
  return context;
};

// Backward compatibility
export const useSimpleAuth = useSupabaseAuth;